import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Client } from 'types';
import { ReportDownloadStatus } from 'enums/reports';
import { DefaultFormValues, ReportForm } from '../types';

const useStyles = makeStyles(() => ({
  infoDisplay: {
    width: '80%',
  },
  collapsableContent: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    justifyContent: 'space-between',
  },
  inputFieldsWrapper: {
    width: '55%',
  },
  clientInputField: {
    width: '45%',
  },
  yearInputField: {
    width: '20%',
    margin: '0 20px',

    '& input': {
      cursor: 'pointer',
    },
  },
  monthInputField: {
    width: '25%',

    '& input': {
      textTransform: 'capitalize',
      cursor: 'pointer',
    },
  },
  downloadButton: {
    marginRight: '10px',
  },
}));

type DownloadReportCardProps = {
  collapseTitle: string;
  formId: string;
  closedMonthsBoundaries: { firstClosureDate: Date; lastClosureDate: Date } | null;
  selectClient: boolean;
  clients?: Array<Client> | null;
  yupSchemaShape: ObjectShape;
  defaultFormValue: DefaultFormValues;
  handleClickDownload: (sortingCenterId: string, form: ReportForm) => Promise<void>;
};

const DownloadReportCard: React.FC<DownloadReportCardProps> = ({
  collapseTitle,
  formId,
  handleClickDownload,
  closedMonthsBoundaries,
  selectClient,
  clients,
  yupSchemaShape,
  defaultFormValue,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const context = useContext(AppContext);
  const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;
  const [downloadStatus, setDownloadStatus] = useState<ReportDownloadStatus>(ReportDownloadStatus.INITIAL);

  const onSubmit = async ({ year, month, client }: DefaultFormValues) => {
    if (!sortingCenterId) return;
    if (!month || !year) return;

    setDownloadStatus(ReportDownloadStatus.PENDING);

    handleClickDownload(sortingCenterId, { year, month, client })
      .then(() => {
        setDownloadStatus(ReportDownloadStatus.SUCCESS);
        notify({
          message: t('reports.download.success.long'),
          severity: 'success',
        });
      })
      .catch(() => {
        setDownloadStatus(ReportDownloadStatus.ERROR);
        notify({
          message: t('reports.download.error'),
          severity: 'error',
        });
      });
  };

  const schema = yup.object().shape(yupSchemaShape);

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: defaultFormValue,
    resolver: yupResolver(schema, { abortEarly: false }),
  });

  const watchConvertedYear = watch('year')?.getFullYear();
  const watchConvertedMonth = watch('month')?.getMonth();
  const сonvertedDate = useMemo(
    () =>
      !!watchConvertedYear && (!!watchConvertedMonth || 0 === watchConvertedMonth)
        ? new Date(watchConvertedYear, watchConvertedMonth, 1)
        : null,
    [watchConvertedYear, watchConvertedMonth],
  );

  useEffect(() => reset(defaultFormValue), [reset, defaultFormValue]);

  useEffect(() => {
    if (
      // check if the watchConverted year and month correspond to a closed month
      // if !closedMonthsBoundaries === true then there are no closed month yet
      сonvertedDate &&
      (!closedMonthsBoundaries ||
        (closedMonthsBoundaries &&
          (сonvertedDate > closedMonthsBoundaries.lastClosureDate ||
            сonvertedDate < closedMonthsBoundaries.firstClosureDate)))
    ) {
      setDownloadStatus(ReportDownloadStatus.NOT_PROCESSABLE);
    } else {
      setDownloadStatus(ReportDownloadStatus.INITIAL);
    }
  }, [watchConvertedYear, watchConvertedMonth, closedMonthsBoundaries, сonvertedDate]);

  return (
    <CollapsableCard
      maxWidth="942px"
      collapsableContent={
        <form id={formId} className={classes.collapsableContent} onSubmit={handleSubmit(onSubmit)}>
          <div>
            {selectClient && (
              <Controller
                name="client"
                control={control}
                render={({ field: { ref, ...inputProps }, fieldState }) => (
                  <SelectWithErrorField
                    inputRef={ref}
                    id="client"
                    error={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    disabled={downloadStatus === ReportDownloadStatus.PENDING}
                    className={classes.clientInputField}
                    placeholder={t('reports.client.name')}
                    choices={clients}
                    choiceDisplayKey="shortName"
                    choiceValueKey="id"
                    variant="standard"
                    {...inputProps}
                  />
                )}
              />
            )}
            <Controller
              name="year"
              control={control}
              render={({ field: { ref, ...inputProps }, fieldState }) => (
                <DatePicker
                  id="year"
                  variant="inline"
                  disableFuture
                  disableToolbar
                  autoOk
                  disabled={downloadStatus === ReportDownloadStatus.PENDING}
                  inputRef={ref}
                  className={classes.yearInputField}
                  placeholder={t('reports.year')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  views={['year']}
                  minDate={new Date(2021)}
                  {...inputProps}
                />
              )}
            />
            <Controller
              name="month"
              control={control}
              render={({ field: { ref, ...inputProps }, fieldState }) => (
                <DatePicker
                  id="month"
                  autoOk
                  variant="inline"
                  format="MMMM"
                  disableToolbar
                  disabled={downloadStatus === ReportDownloadStatus.PENDING}
                  inputRef={ref}
                  className={classes.monthInputField}
                  placeholder={t('reports.month')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  views={['month']}
                  {...inputProps}
                />
              )}
            />
          </div>
          <div>
            {downloadStatus === ReportDownloadStatus.INCOMING_SOON && (
              <Typography color="error">{t('reports.download.incomingSoon')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.NOT_PROCESSABLE && (
              <Typography color="error">{t('reports.download.nonClosedMonth')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.SUCCESS && (
              <Typography color="primary">{t('reports.download.success.short')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.PENDING && <CircularProgress size={25} />}
          </div>
          <div>
            <Button
              size="small"
              variant="outlined"
              className={classes.downloadButton}
              type="submit"
              form={formId}
              disabled={
                downloadStatus === ReportDownloadStatus.INCOMING_SOON ||
                downloadStatus === ReportDownloadStatus.PENDING ||
                downloadStatus === ReportDownloadStatus.NOT_PROCESSABLE
              }
            >
              {t('reports.button.download')}
            </Button>
          </div>
        </form>
      }
    >
      <Typography className={classes.infoDisplay}>{collapseTitle}</Typography>
    </CollapsableCard>
  );
};
export default DownloadReportCard;
